import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const AccountHeadModal = ({ 
  open, 
  onClose, 
  onSave, 
  accountHeadData = null, 
  mode = 'create', // 'create', 'edit', 'view'
  balanceSheetHeads = [],
  existingAccountHeads = [] // Add this prop to receive the list of existing account heads
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  
  const [formData, setFormData] = useState({
    BalanceSheetHeadId: '',
    Name: '',
    IsType: true // true for Income, false for Expense
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'accountHeadData:', accountHeadData);
      
      if (accountHeadData && mode !== 'create') {
        console.log('Loading existing account head data for edit/view, ID:', accountHeadData.Id);
        setFormData({
          Id: accountHeadData.Id || '',
          BalanceSheetHeadId: accountHeadData.BalanceSheetHeadId || '',
          Name: accountHeadData.Name || '',
          IsType: accountHeadData.IsType !== undefined ? accountHeadData.IsType : true
        });
      } else {
        console.log('Creating new account head');
        // Reset form for create mode
        setFormData({
          BalanceSheetHeadId: '',
          Name: '',
          IsType: true
        });
      }
      setErrors({});
    }
  }, [open, accountHeadData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRadioChange = (e) => {
    setFormData(prev => ({
      ...prev,
      IsType: e.target.value === 'true'
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Name.trim()) {
      newErrors.Name = 'Account head name is required';
    }
    
    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateAccountHead = () => {
    // Check if an account head with the same name exists in the list (case-insensitive)
    const duplicate = existingAccountHeads.find(accountHead => {
      // Skip the current account head in edit mode
      if (mode === 'edit' && accountHeadData && accountHead.Id === accountHeadData.Id) {
        return false;
      }
      
      // Check if name matches (case-insensitive)
      const nameMatch = accountHead.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      
      return nameMatch;
    });
    
    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate account head
    const duplicate = checkDuplicateAccountHead();
    if (duplicate) {
      setDuplicateMessage(`An account head with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
        Name: formData.Name,
        IsType: formData.IsType,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        // Validate that we have the account head ID
        if (!accountHeadData || !accountHeadData.Id) {
          console.error('Cannot update: Missing account head ID');
          alert('Error: Cannot update account head - missing ID');
          return;
        }
        dataToSend.Id = accountHeadData.Id;
        // For edit mode, add modification fields but don't include ID in body (it's in URL)
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (accountHeadData.CreatedBy) dataToSend.CreatedBy = accountHeadData.CreatedBy;
        if (accountHeadData.CreatedDate) dataToSend.CreatedDate = accountHeadData.CreatedDate;
        
        console.log('Updating account head with ID:', accountHeadData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /AccountHeadApi/' + accountHeadData.Id);
        
        const response = await updateData('AccountHeadApi', accountHeadData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        
        console.log('Creating new account head');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /AccountHeadApi');
        
        const response = await postData('AccountHeadApi', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving account head:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Account Head';
      case 'edit': return 'Edit Account Head';
      case 'view': return 'Account Head Details';
      default: return 'Account Head';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '8px', fontSize: '14px', height: '40px' };
  const labelStyle = { fontSize: '13px' };
  const helperStyle = { fontSize: '11px', margin: '4px 0 0 0' };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AccountBalanceIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ 
          padding: '16px 20px', 
          paddingBottom: '8px'
        }}>
          <Typography variant="subtitle2" style={{ 
            color: '#666', 
            marginBottom: '16px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            {mode === 'create' ? 'Add a new account head to your chart of accounts' : 
             mode === 'edit' ? 'Update account head information' : 
             'View account head details'}
          </Typography>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '8px',
                marginTop: '4px',
                color: '#2d3748',
                fontSize: '14px'
              }}>
                Account Head Information
              </Typography>
            </Grid>

            {/* Account Head Name */}
            <Grid item xs={12} style={{ marginBottom: '8px' }}>
              <TextField
                name="Name"
                label="Account Head Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter account head name"
                InputProps={{ 
                  style: { 
                    ...inputStyle, 
                    height: '40px',
                    fontSize: '14px'
                  } 
                }}
                InputLabelProps={{ 
                  style: { 
                    ...labelStyle, 
                    fontSize: '13px'
                  } 
                }}
                FormHelperTextProps={{ 
                  style: { 
                    ...helperStyle, 
                    fontSize: '11px',
                    marginTop: '4px'
                  } 
                }}
                style={{ marginBottom: '4px' }}
              />
            </Grid>

            {/* Balance Sheet Head */}
            <Grid item xs={12} style={{ marginBottom: '8px' }}>
              <TextField
                select
                name="BalanceSheetHeadId"
                label="Balance Sheet Head *"
                value={formData.BalanceSheetHeadId}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.BalanceSheetHeadId}
                helperText={errors.BalanceSheetHeadId}
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ 
                  style: { 
                    ...inputStyle, 
                    height: '40px',
                    fontSize: '14px'
                  } 
                }}
                InputLabelProps={{ 
                  style: { 
                    ...labelStyle, 
                    fontSize: '13px'
                  } 
                }}
                FormHelperTextProps={{ 
                  style: { 
                    ...helperStyle, 
                    fontSize: '11px',
                    marginTop: '4px'
                  } 
                }}
                style={{ marginBottom: '4px' }}
              >
                <option value=""></option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id} value={head.Id}>{head.Name}</option>
                ))}
              </TextField>
            </Grid>

            {/* Type Selection */}
            <Grid item xs={12} style={{ marginTop: '12px', marginBottom: '8px' }}>
              <FormControl component="fieldset" disabled={isReadOnly}>
                <FormLabel component="legend" style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '12px'
                }}>
                  Account Type *
                </FormLabel>
                <RadioGroup
                  row
                  name="IsType"
                  value={formData.IsType.toString()}
                  onChange={handleRadioChange}
                  style={{ gap: '24px', marginTop: '8px' }}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio size="small" style={{ color: '#2e7d32' }} />}
                    label={
                      <Typography style={{ fontSize: '14px', fontWeight: '500' }}>
                        Income
                      </Typography>
                    }
                    style={{
                      margin: 0,
                      padding: '8px 16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: formData.IsType ? '#e8f5e8' : 'white',
                      minWidth: '120px'
                    }}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio size="small" style={{ color: '#f57c00' }} />}
                    label={
                      <Typography style={{ fontSize: '14px', fontWeight: '500' }}>
                        Expense
                      </Typography>
                    }
                    style={{
                      margin: 0,
                      padding: '8px 16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: !formData.IsType ? '#fff3e0' : 'white',
                      minWidth: '120px'
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ 
          padding: '16px 20px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '12px',
          minHeight: 'auto'
        }}>
          <Button 
            onClick={onClose}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '14px',
              padding: '8px 16px',
              minHeight: '36px'
            }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button 
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '6px',
                padding: '8px 20px',
                fontSize: '14px',
                minHeight: '36px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Account Head' : 'Update Account Head'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Duplicate Warning Dialog */}
    <Dialog
      open={duplicateDialogOpen}
      onClose={() => setDuplicateDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon 
          style={{ 
            fontSize: '64px', 
            color: '#ff9800',
            marginBottom: '16px'
          }} 
        />
        <Typography 
          variant="h6" 
          style={{ 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}
        >
          Duplicate Account Head Found
        </Typography>
        <Typography 
          style={{ 
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {duplicateMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setDuplicateDialogOpen(false)}
          variant="contained"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '8px 32px',
            fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default AccountHeadModal;